import { IWebsocketMessage } from 'types';
import { noop } from './utilities';

interface ConnectionOptions {
  reConnectOnClose?: boolean;
  onMessage: (data: string) => void;
}

export class Connection {
  wsUrl!: string;
  ws: WebSocket | undefined;
  isOpen: boolean = false;
  handleMessage!: (data: string) => void;
  retryTimeout: number = 250;
  reconnectAttempts: number = 0;
  maxRetry: number = 5;
  reConnectOnClose: boolean = false;
  static instance: Connection | undefined;

  constructor(wsUrl: string, options: ConnectionOptions) {
    if (Connection.instance) {
      return Connection.instance;
    }
    Connection.instance = this;

    if (!wsUrl) {
      throw new Error('No websocket url passed through!');
    }
    this.wsUrl = wsUrl;
    this.reConnectOnClose = typeof options.reConnectOnClose === 'boolean' ? options.reConnectOnClose : false;
    this.handleMessage = typeof options.onMessage === 'function' ? options.onMessage : noop;
  }

  async connect() {
    if (Connection.instance?.ws) {
      return Connection.instance;
    }
    try {
      this.ws = new WebSocket(this.wsUrl); // e.g. 'wss://localhost:8080'

      this.ws.addEventListener('open', () => {
        this.isOpen = true;
      });

      this.ws.addEventListener('message', (evt: MessageEvent) => {
        this.handleMessage(evt.data);
      });

      this.ws.addEventListener('close', (evt: CloseEvent) => {
        this.isOpen = false;
        this.reconnectAttempts += 1;
        this.ws = undefined; // ready for garbage collection

        if (this.reConnectOnClose && this.reconnectAttempts <= this.maxRetry) {
          this.retryTimeout = Math.min(10000, this.retryTimeout + this.retryTimeout * this.reconnectAttempts);

          console.log(`Socket is closed. Attempting to reconnect in ${this.retryTimeout}...`, evt.reason);
          setTimeout(() => {
            this.connect();
          }, this.retryTimeout);
        }
      });

      this.ws.addEventListener('error', (evt: Event) => {
        console.error('Socket encountered error: ', evt, 'Closing socket...');
        this.ws?.close();
      });

      await waitForSocketState(this.ws, this.ws.OPEN);
      return this;
    } catch (err) {
      console.log('Error connectiing to websocket');
    }
  }

  sendMessage(wsMsg: IWebsocketMessage) {
    console.log(`Connection sending message to channel: ${JSON.stringify(wsMsg)}`);
    this.ws?.send(JSON.stringify({ ...wsMsg }));
    console.log('message sent!');
  }

  close() {
    this.ws?.close();
    this.isOpen = false;
    this.ws = undefined;
  }

  static clean() {
    Connection.instance?.close();
    Connection.instance = undefined;
  }
}

export function waitForSocketState(socket: WebSocket, state: number) {
  return new Promise(function (resolve) {
    setTimeout(function () {
      if (socket.readyState === state) {
        resolve(true);
      } else {
        waitForSocketState(socket, state).then(resolve);
      }
    }, 5);
  });
}
